import { SelectProps } from '@cloudscape-design/components';

export type StatisticStatus = 'extremely-low' | 'below-average' | 'above-average' | 'average';

export type PlaceContactBase = {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  title?: string;
  department?: string;
  date?: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  hidden?: boolean;
};

export type Task = {
  uuid: string;
  name:
    | 'collect_details'
    | 'collect_maps'
    | 'collect_reviews'
    | 'extract_contacts'
    | 'organize_contacts'
    | 'collect_web_contacts'
    | 'report_statistics';
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  retries?: number;
};

export const TASK_NAME_MAP: Record<Task['name'], string> = {
  collect_details: 'Collecting details...',
  collect_maps: 'Collecting maps...',
  collect_reviews: 'Collecting reviews...',
  extract_contacts: 'Extracting contacts...',
  organize_contacts: 'Organizing contacts...',
  collect_web_contacts: 'Crawling website...',
  report_statistics: 'Generating statistics...',
};

export const TASK_NAME_INFO_MAP: Record<Task['name'], string> = {
  collect_details: 'Collecting details from Google Maps...',
  collect_maps: 'Collecting map information from Google Maps...',
  collect_reviews: 'Collecting reviews from Google Maps...',
  extract_contacts: 'Extracting contact information from reviews...',
  organize_contacts: 'Organizing contact information...',
  collect_web_contacts: 'Crawling website for contact information...',
  report_statistics: 'Calculating scorecard statistics...',
};

export const REPORT_CARD_TASKS: Task['name'][] = [
  'collect_details',
  'collect_maps',
  'collect_reviews',
  'report_statistics',
];

export const BUSINESS_TYPES: SelectProps.Option[] = [
  { label: 'Automotive', value: 'Automotive' },
  { label: 'Fitness', value: 'Fitness' },
  { label: 'Grocery', value: 'Grocery' },
  { label: 'Hotel', value: 'Hotel' },
  { label: 'Medical Offices', value: 'Medical Offices' },
  { label: 'Restaurants', value: 'Restaurants' },
  { label: 'Retail Stores', value: 'Retail Stores' },
  { label: 'Spas', value: 'Spas' },
  { label: 'Other', value: 'Other' },
];

export type PlaceBasic = {
  place_id: string;
  name: string;
  latitude: number;
  longitude: number;
  address: string;
  cid_url?: string;
  phone_number?: string;
  website?: string;
  rating?: number;
  business_status?: string;
  total_review_count?: number;
  utc_offset?: number;
  created_at: string;
  updated_at: string;
  uuid: string;
  hidden?: boolean;
};

export type ReportContactRequest = {
  first_name: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  title?: string;
  reference_name?: string;
};

export type CreateReportRequest = {
  place_id: string;
  contact?: ReportContactRequest;
  business_type?: string;
};

export type CreateReportsRequest = {
  place_ids: string[];
  contact?: ReportContactRequest;
  business_type?: string;
};

export type CreateReportPublicRequest = {
  place_id: string;
  contact: ReportContactRequest;
  rep_uuid?: string;
  business_type?: string;
};

export type CreateReportResponse = {
  uuid: string;
};

export type CreateReportPublicResponse = {
  public_code: string;
};

export type ReviewKeyword = {
  keyword: string;
  polarity: 'positive' | 'negative' | 'neutral';
  count: number;
};

export type PlacePeriodReport = {
  uuid: string;
  created_at: string;
  // 30-days, 60-days, 90-days, 180-days
  range: string;
  start_date: string;
  end_date: string;
  place_letter_grade: string;
  place_percentile: number;
  average_rating: number;
  average_rating_status: StatisticStatus;
  average_rating_percentile: number;
  review_count: number;
  review_count_status: StatisticStatus;
  review_count_percentile: number;
  response_rate: number;
  response_rate_status: StatisticStatus;
  response_rate_percentile: number;
  avg_response_time: string;
  avg_response_time_status: StatisticStatus;
  avg_response_time_percentile: number;
  keywords?: ReviewKeyword[];
};

export type PlaceReport = {
  report_at: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  tasks: Task[];
  place?: PlaceBasic;
  periods?: PlacePeriodReport[];
  reviews?: ReviewBasic[];
  user?: User;
};

export type PlacePeriodReportPublic = {
  uuid: string;
  created_at: string;
  range: string;
  start_date: string;
  end_date: string;
  place_letter_grade: string;
  place_percentile: number;
};

export type PlaceReportPublic = {
  report_at: string;
  tasks: Task[];
  place?: PlaceBasic;
  periods: PlacePeriodReportPublic[];
};

export type LoadedPlaceReport = {
  report_at: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  tasks: Task[];
  place: PlaceBasic;
  periods: PlacePeriodReport[];
  reviews?: ReviewBasic[];
};

export type LoadedPlaceReportPublic = {
  report_at: string;
  tasks: Task[];
  place: PlaceBasic;
  periods: PlacePeriodReportPublic[];
};

export type Place = PlaceBasic & {
  contacts: PlaceContactBase[];
  tasks: Task[];
  reviews: ReviewBasic[];
};

export type SearchRequest = {
  names: string;
  include_services?: boolean;
  include_parts?: boolean;
};

export type CollectRequest = {
  place_ids: string[];
  list_names?: string[];
  crawl?: boolean;
  reviews?: boolean;
};

export type PlaceSearchItem = {
  name: string;
  place_id: string;
  formatted_address: string;
};

export type SearchItem = {
  name: string;
  places: PlaceSearchItem[];
};

export type PlaceSearchItemWithName = PlaceSearchItem & {
  search_name: string;
};

export type PlaceMeta = {
  total: number;
  start?: string;
  end?: string;
  rating_min?: number;
  rating_max?: number;
  business_statuses?: string[];
  list_names?: string[];
};

export type PlaceRequest = {
  place_uuids: string[];
};

export type PlaceList = {
  name: string;
  description?: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  places: PlaceBasic[];
  hidden?: boolean;
};

export type PlaceListCreate = {
  name: string;
  description?: string;
};

export type PlaceContactMeta = {
  total: number;
  start?: string;
  end?: string;
  list_names?: string[];
  place_names?: string[];
};

export type PlaceListMeta = {
  total: number;
  start?: string;
  end?: string;
  list_names?: string[];
};

export type ReviewContactBasic = {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  title?: string;
  department?: string;
  date?: string;
  uuid: string;
  created_at: string;
  updated_at: string;
};

export type PlaceContact = PlaceContactBase & {
  place: PlaceBasic;
  review_contacts: ReviewContactBasic[];
};

export type ReviewBasic = {
  review_id: string;
  language?: string;
  recency?: string;
  stars?: number;
  content?: string;
  response_language?: string;
  response_recency?: string;
  response_content?: string;
  type?: string;
  uuid: string;
  created_at: string;
  updated_at: string;
  review_date?: string;
  response_date?: string;
};

export type ReviewContact = ReviewContactBasic & {
  review: ReviewBasic;
};

export type PlaceContactWithReviews = PlaceContact & {
  review_contacts: ReviewContact[];
};

export type SMSConsentUpdate = {
  phone_number: string;
  consent_status: 'optIn' | 'optOut';
};

export type SMSConsent = {
  uuid: string;
  phone_number: string;
  consent_status: 'optIn' | 'optOut';
  created_at: string;
  updated_at: string;
};

export type SMSSessionStartSuggestion = {
  uuid: string;
  message: string;
  rank: number;
  model: string;
  selected: boolean;
  created_at: string;
  updated_at: string;
};

export type SMSMessageSuggestion = {
  uuid: string;
  message: string;
  rank: number;
  model: string;
  selected: boolean;
  created_at: string;
  updated_at: string;
};

export type User = {
  uuid: string;
  user_id: string;
  email: string;
  name: string;
};

export type SMSMessage = {
  uuid: string;
  message: string;
  author: 'client' | 'assistant' | 'user';
  sent_at?: string;
  status: 'pending' | 'sent' | 'failed' | 'received';
  created_at: string;
  updated_at: string;
  suggestions: SMSMessageSuggestion[];
  user?: User;
};

export type SMSSession = {
  uuid: string;
  phone_number: string;
  status: 'active' | 'optOut';
  created_at: string;
  updated_at: string;
  messages: SMSMessage[];
  suggestions: SMSSessionStartSuggestion[];
  place_contact?: PlaceContact;
};

export type SMSMessageSendRequest = {
  message?: string;
  suggestion_uuid?: string;
  suggestion_type?: 'session' | 'message';
};

export type WebsiteContact = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  title: string;
  department: string;
  created_at: string;
  updated_at: string;
};

export type DripEmailBasic = {
  uuid: string;
  created_at: string;
  updated_at: string;
  scheduled_at?: string;
  cancelled_at?: string;
  sent_at?: string;
  opened_at?: string;
  clicked_at?: string;
  status?: string;
};

export type DripContactBasic = {
  uuid: string;
  created_at: string;
  updated_at: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title?: string;
  department?: string;
  crm_id?: string;
  opt_out: boolean;
  opt_out_at?: string;
  place?: PlaceBasic;
};

export type DripContact = DripContactBasic & {
  emails: DripEmailBasic[];
};

export type DripEmail = DripEmailBasic & {
  reviews: ReviewBasic[];
};

export type DripEmailWithContact = DripEmailBasic & {
  contact: DripContactBasic;
};

export type DripEmailWithReviews = DripEmailWithContact & {
  reviews: ReviewBasic[];
};

export type DripContactOptOutRequest = {
  opt_out: boolean;
};

export type DripEmailReportInfo = {
  uuid: string;
};

export type DripContactOptOutResponse = {
  opt_out: boolean;
};
